import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Select } from 'primeng/select';
import { Message } from 'primeng/message';
import { map, switchMap, take } from 'rxjs';
import { ScannerComponent } from './scanner/scanner.component';
import { LightTagComponent } from './lighttag/lighttag.component';
import { MdmComponent } from './mdm/mdm.component';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { OrganizationService } from '@nimmsta/ng-commons-lib/services/organization';
import { ParentOrganization } from '@nimmsta/ng-commons-lib/models';
import {
  ReusableTabsComponent,
  TabsConfiguration,
} from '@nimmsta/ng-commons-lib/reusable-tabs';

_('device.title');
_('device.organizationFilter');

@Component({
  templateUrl: 'device.component.html',
  styleUrl: 'device.component.scss',
  selector: 'app-device',
  imports: [
    Message,
    ErrorComponent,
    FormsModule,
    NgClass,
    TranslatePipe,
    Select,
    ScannerComponent,
    LightTagComponent,
    MdmComponent,
    HeaderBarComponent,
    ReusableTabsComponent,
  ],
})
export class DeviceComponent implements OnInit, AfterViewInit {
  @ViewChild('scannerTemplate', { static: true })
  scannerTemplate!: TemplateRef<unknown>;
  @ViewChild('lighttagTemplate', { static: true })
  lighttagTemplate!: TemplateRef<unknown>;
  @ViewChild('mdmTemplate', { static: true })
  mdmTemplate!: TemplateRef<unknown>;

  organizations: ParentOrganization[] = [];

  selectedOrganizationId?: string;

  hasChildOrganizations = false;

  organizationsLoading = true;

  error?: HttpErrorResponse;

  tabsConfig: TabsConfiguration = {
    tabs: [],
    defaultTab: 'scanner',
    storageKey: 'lastTabDevice',
    enableRouting: true,
  };

  private cdr = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private organizationService = inject(OrganizationService);

  get organizationFilterId(): string | undefined {
    return this.hasChildOrganizations ? this.selectedOrganizationId : undefined;
  }

  ngOnInit(): void {
    this.authService
      .getCurrentUser()
      .pipe(
        take(1),
        switchMap((user) =>
          this.organizationService.getDeviceScope().pipe(
            map((scope) => ({
              currentOrganizationId:
                user.currentOrganization?.id ?? user.organization.id,
              organizations: scope.organizations,
            })),
          ),
        ),
      )
      .subscribe({
        next: ({ currentOrganizationId, organizations }) => {
          const currentOrganization = organizations.find(
            (organization) => organization.id === currentOrganizationId,
          );
          const childOrganizations = organizations
            .filter((organization) => organization.id !== currentOrganizationId)
            .sort((left, right) => left.name.localeCompare(right.name));
          this.hasChildOrganizations = childOrganizations.length > 0;
          this.organizations = currentOrganization
            ? [currentOrganization, ...childOrganizations]
            : childOrganizations;
          this.selectedOrganizationId =
            currentOrganization?.id ?? this.organizations[0]?.id;
          this.organizationsLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
          this.organizationsLoading = false;
        },
      });
  }

  ngAfterViewInit(): void {
    this.tabsConfig = {
      ...this.tabsConfig,
      tabs: [
        {
          id: 'scanner',
          labelKey: 'scanner.title',
          template: this.scannerTemplate,
        },
        {
          id: 'lighttag',
          labelKey: 'lighttag.title',
          template: this.lighttagTemplate,
        },
        { id: 'mdm', labelKey: 'mdm.title', template: this.mdmTemplate },
      ],
    };
    this.cdr.detectChanges();
  }
}
